import { create } from 'zustand';

import type { MemberTestFailure } from './member-test-logic';

/**
 * Plan 2026-10-15-003 U3 (§4.4) — which person's test panel is open on the
 * Review step, and each person's latest test dispatch id.
 *
 * Why a module-scope store: the panel must survive the Glaze `TabPanel`
 * unmount on a tab switch (a rep who flips to Sequences mid-test and back
 * still sees the result), and `ReviewStep` has no ancestor to hold it. Nothing
 * here is seeded from server data, so no `useDraftState`.
 *
 * `PlayFlowShell` calls {@link clearMemberTests} with the other play-scoped
 * stores — a previous Play's open person and tests must not leak into the next.
 */
interface MemberTestState {
  /** Whose side panel is open (Review only). */
  openMemberId: string | null;
  /** Which folder it was opened from (`null` for "Not in any group"). */
  openRungId: string | null;
  /** Requests in flight, keyed by {@link memberTestKey} (play + person). */
  pendingKeys: Record<string, true>;
  /** Latest test per person per play, keyed by {@link memberTestKey}. */
  testsByKey: Record<string, { dispatchId: string; startedAt: string }>;
  /**
   * The words a refused start left for a person, keyed by {@link memberTestKey}.
   * In the store (not component state) so a refusal from a row's Test button is
   * the sentence the panel shows when it opens for that person.
   */
  failuresByKey: Record<string, MemberTestFailure>;
  /**
   * The exact Dispatch-log row a person's panel was opened on, keyed by
   * {@link memberTestKey} (play + person — a late result for another play must
   * not clear it) (plan 2026-10-15-004 §6.5) — set by clicking a name in the "All people"
   * table, absent when the panel was opened the plain way (it then shows the
   * person's latest test). `logId` names the row; `dispatchId`/`isTest` say
   * which send it belongs to and how to label it.
   */
  focusByMember: Record<string, MemberResultFocus>;
}

export interface MemberResultFocus {
  dispatchId: string;
  logId: string;
  isTest: boolean;
}

/**
 * Pending and recorded tests are scoped to the play: a POST that settles after
 * the rep moved to another play lands under the OLD play's key, where the new
 * play never reads it (and `clearMemberTests` cannot be undone by a late write).
 */
export function memberTestKey(playId: string, memberId: string): string {
  return `${playId}:${memberId}`;
}

/** Exported per the store convention — reset idiom is `useMemberTestStore.setState(initialState, true)`. */
export const initialState: MemberTestState = {
  openMemberId: null,
  openRungId: null,
  pendingKeys: {},
  testsByKey: {},
  failuresByKey: {},
  focusByMember: {},
};

export const useMemberTestStore = create<MemberTestState>(() => ({ ...initialState }));

/**
 * Drops a person's focus: for ONE play when `playId` is given, else for that
 * person in every play (the panel was opened/closed the plain way — it holds
 * no play id).
 */
function withoutFocus(
  focusByMember: Record<string, MemberResultFocus>,
  memberId: string | null,
  playId?: string,
): Record<string, MemberResultFocus> {
  if (memberId === null) return focusByMember;
  const matches = (key: string): boolean =>
    playId === undefined ? key.endsWith(`:${memberId}`) : key === memberTestKey(playId, memberId);
  const kept = Object.entries(focusByMember).filter(([key]) => !matches(key));
  return kept.length === Object.keys(focusByMember).length ? focusByMember : Object.fromEntries(kept);
}

/** Opens a person's panel on their latest test (any focus a row click left is dropped). */
export function openMemberTest(memberId: string, rungId: string | null): void {
  useMemberTestStore.setState((state) => ({
    openMemberId: memberId,
    openRungId: rungId,
    focusByMember: withoutFocus(state.focusByMember, memberId),
  }));
}

/** Opens a person's panel focused on one exact Dispatch-log row. */
export function openMemberResult(
  playId: string,
  memberId: string,
  rungId: string | null,
  focus: MemberResultFocus,
): void {
  useMemberTestStore.setState((state) => ({
    openMemberId: memberId,
    openRungId: rungId,
    focusByMember: { ...state.focusByMember, [memberTestKey(playId, memberId)]: focus },
  }));
}

/** Closes the panel; recorded tests stay so reopening shows the result. */
export function closeMemberTest(): void {
  useMemberTestStore.setState((state) => ({
    openMemberId: null,
    openRungId: null,
    focusByMember: withoutFocus(state.focusByMember, state.openMemberId),
  }));
}

export function markTestPending(playId: string, memberId: string): void {
  useMemberTestStore.setState((state) => ({
    pendingKeys: { ...state.pendingKeys, [memberTestKey(playId, memberId)]: true },
  }));
}

/** Clears ONLY the person whose request settled — another person's request may still be running. */
export function clearTestPending(playId: string, memberId: string): void {
  useMemberTestStore.setState((state) => {
    const { [memberTestKey(playId, memberId)]: _settled, ...rest } = state.pendingKeys;
    return { pendingKeys: rest };
  });
}

/** Records (replacing) a person's latest test dispatch for this play. */
export function recordMemberTest(playId: string, memberId: string, dispatchId: string): void {
  useMemberTestStore.setState((state) => ({
    // A new test supersedes whichever older row the panel was focused on.
    focusByMember: withoutFocus(state.focusByMember, memberId, playId),
    testsByKey: {
      ...state.testsByKey,
      [memberTestKey(playId, memberId)]: { dispatchId, startedAt: new Date().toISOString() },
    },
  }));
}

/** Keeps a refused start's words for this person (replacing any earlier ones). */
export function recordMemberFailure(playId: string, memberId: string, failure: MemberTestFailure): void {
  useMemberTestStore.setState((state) => ({
    failuresByKey: { ...state.failuresByKey, [memberTestKey(playId, memberId)]: failure },
  }));
}

/** Drops this person's failure words (the next start, or an adopted test). */
export function clearMemberFailure(playId: string, memberId: string): void {
  useMemberTestStore.setState((state) => {
    const { [memberTestKey(playId, memberId)]: _cleared, ...rest } = state.failuresByKey;
    return { failuresByKey: rest };
  });
}

/** The play-scoped reset `PlayFlowShell` performs. */
export function clearMemberTests(): void {
  useMemberTestStore.setState({ ...initialState });
}
